import { useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * Render only the table rows that are on screen.
 *
 * Why (Max, 2026-09-08): Product Line took 16.1 SECONDS to show anything, and its api had finished
 * at 2.3 — the rest was React laying out 8,842 rows of 26 columns, 327,493 DOM nodes, for a box that
 * shows twenty-one of them. Style Master had the same shape at 10,707 rows and ~5 s.
 *
 * The arithmetic is only honest when every row is the SAME height, which is true of both tables
 * because every cell in them is one nowrap line — 35px on Product Line, 32px on Style Master, both
 * measured rather than assumed. The spacer above and below the slice is exactly the skipped rows'
 * height, so the scrollbar remains the true length of the list. What is given up is the browser's
 * own find-on-page, which only ever searched the rendered rows anyway.
 *
 * One hook, two callers. Copying the arithmetic into each screen is how two tables that must agree
 * about the same thing stop agreeing.
 *
 *   const [boxRef, win] = useRowWindow({ rowHeight: 35, total: rows.length, resetKey: sortAndFilters });
 *   <div ref={boxRef}> … {win.padTop > 0 && spacer(win.padTop)}
 *                         {rows.slice(win.first, win.first + win.count).map(…)}
 *                         {win.padBottom > 0 && spacer(win.padBottom)}
 */
export default function useRowWindow({ rowHeight, total, min = 200, overscan = 8, resetKey = '' }) {
  const boxRef = useRef(null);
  const [view, setView] = useState({ top: 0, height: 720 });
  const windowed = total > min;

  useEffect(() => {
    const el = boxRef.current;
    if (!el || !windowed) return undefined;
    const read = () => setView((prev) => {
      const next = { top: el.scrollTop, height: el.clientHeight || 720 };
      return prev.top === next.top && prev.height === next.height ? prev : next;
    });
    read();
    el.addEventListener('scroll', read, { passive: true });
    window.addEventListener('resize', read);
    return () => { el.removeEventListener('scroll', read); window.removeEventListener('resize', read); };
  }, [windowed]);

  /* A new sort or filter is a different list, and the offset you had in the old one points at a gap
     in the new one — the rows would come back only on the next scroll event. Layout effect, so the
     jump happens before paint rather than as a visible flick. */
  useLayoutEffect(() => {
    if (boxRef.current) boxRef.current.scrollTop = 0;
    setView((prev) => (prev.top === 0 ? prev : { ...prev, top: 0 }));
  }, [resetKey, total]);

  if (!windowed) {
    return [boxRef, { windowed: false, first: 0, count: total, padTop: 0, padBottom: 0 }];
  }
  const first = Math.max(0, Math.floor(view.top / rowHeight) - overscan);
  const count = Math.min(total - first, Math.ceil(view.height / rowHeight) + overscan * 2);
  return [boxRef, {
    windowed: true,
    first,
    count,
    padTop: first * rowHeight,
    padBottom: Math.max(0, (total - first - count) * rowHeight),
  }];
}
